import { Eye } from "lucide-react";
import brandLogoMark from "@/assets/brand-logo-mark.png";
import type { Invoice, InvoiceLineItem, InvoiceWithTotals } from "@/features/invoices/types";
import { TERMS_LABELS } from "@/features/invoices/lib/terms";
import { formatShortDate } from "@/lib/format";
import { cn, formatCents } from "@/lib/utils";

interface InvoicePreviewProps {
  invoice: Invoice;
  lineItems: InvoiceLineItem[];
  totals: Pick<InvoiceWithTotals, "subtotalCents" | "taxAmtCents" | "totalCents">;
  firmName: string;
  className?: string;
}

/** The NSI mark — the paper's firm logo (the brand-logo-mark asset). */
function PaperMark() {
  return (
    <img
      src={brandLogoMark}
      alt=""
      draggable={false}
      className="size-8.5 shrink-0 rounded-[10px] object-contain"
    />
  );
}

/**
 * The client-facing invoice as a PDF-style "paper" — a read-only mirror of the
 * editor, derived entirely from props (no fetch). Not a shadcn Card: it's a
 * bespoke document surface with its own radius/shadow.
 */
export function InvoicePreview({ invoice, lineItems, totals, firmName, className }: InvoicePreviewProps) {
  const isDraft = invoice.status === "DRAFT";

  return (
    <aside aria-label="Invoice preview" className={cn("flex min-w-0 flex-col gap-3", className)}>
      <div className="flex items-center justify-between gap-3 px-1">
        <span className="text-muted-foreground flex items-center gap-2 text-xs font-semibold tracking-[0.08em] uppercase">
          <Eye className="size-3.5" /> Live preview
        </span>
        <span className="text-muted-foreground text-xs">What the client receives · US Letter</span>
      </div>

      {/* Paper */}
      <div className="bg-card relative flex flex-col gap-8 overflow-hidden rounded-[6px] border p-11 text-[13px] shadow-xl">
        {/* Top accent bar: navy 70% + orange 30% */}
        <span aria-hidden className="absolute inset-x-0 top-0 flex h-1.5">
          <span className="bg-brand-panel w-[70%]" />
          <span className="bg-primary flex-1" />
        </span>

        {isDraft && (
          <span
            aria-hidden
            className="border-warning bg-warning-soft text-warning font-heading absolute top-10 -right-8 rotate-[18deg] border px-12 py-1.5 text-[13px] font-bold tracking-[0.3em] uppercase"
          >
            Draft
          </span>
        )}

        {/* Header */}
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center gap-2.5">
              <PaperMark />
              <span className="font-heading text-[17px] font-bold tracking-[-0.01em]">{firmName}</span>
            </div>
            <span className="text-muted-foreground text-xs leading-relaxed">
              [Firm street address]
              <br />
              [City, State ZIP]
              <br />
              [Phone] · [Email]
            </span>
          </div>
          <div className="flex flex-col items-end gap-1.5 text-right">
            <span className="text-brand-panel font-heading text-[30px] leading-none font-semibold tracking-[-0.02em]">
              Invoice
            </span>
            <span className="text-muted-foreground text-xs">
              No. <strong className="text-foreground font-semibold">{invoice.invoiceNumber}</strong>
            </span>
          </div>
        </div>

        {/* Parties */}
        <div className="grid gap-5 border-y py-5 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <span className="text-muted-foreground text-[10px] font-semibold tracking-[0.12em] uppercase">
              Bill to
            </span>
            <span className="leading-relaxed whitespace-pre-line">
              {invoice.billTo || <span className="text-muted-foreground italic">No billing address</span>}
            </span>
          </div>
          <div className="grid grid-cols-[auto_1fr] content-start gap-x-4 gap-y-1.5 text-xs">
            <span className="text-muted-foreground">Invoice date</span>
            <span className="font-semibold tabular-nums">{formatShortDate(invoice.invoiceDate)}</span>
            <span className="text-muted-foreground">Terms</span>
            <span className="font-semibold">{TERMS_LABELS[invoice.terms]}</span>
            <span className="text-muted-foreground">Due date</span>
            <span className="font-semibold tabular-nums">{formatShortDate(invoice.dueDate)}</span>
          </div>
        </div>

        {/* Items */}
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="text-muted-foreground border-brand-panel border-b-2 [&>th]:pb-2.5 [&>th]:text-[10px] [&>th]:font-semibold [&>th]:tracking-[0.12em] [&>th]:uppercase">
              <th className="text-left">Description</th>
              <th className="w-12 text-right">Qty</th>
              <th className="w-20 text-right">Unit price</th>
              <th className="w-20 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-muted-foreground border-b py-5 text-center italic">
                  Line items you add will appear here
                </td>
              </tr>
            ) : (
              lineItems.map((item) => (
                <tr key={item.id} className="border-b [&>td]:py-2.5">
                  <td className="pr-2">{item.description || "—"}</td>
                  <td className="text-right tabular-nums">{item.quantity}</td>
                  <td className="text-right tabular-nums">{formatCents(item.unitPriceCents)}</td>
                  <td className="text-right tabular-nums">{formatCents(item.extendedAmtCents)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="flex w-60 flex-col gap-2 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="tabular-nums">{formatCents(totals.subtotalCents)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tax ({invoice.taxPercent}%)</span>
              <span className="tabular-nums">{formatCents(totals.taxAmtCents)}</span>
            </div>
            <div className="bg-brand-panel mt-1 flex items-baseline justify-between rounded-lg px-3 py-2.5 text-white">
              <span className="font-semibold">Total due</span>
              <span className="font-heading text-lg font-bold tabular-nums">{formatCents(totals.totalCents)}</span>
            </div>
          </div>
        </div>

        {/* Note + Footer */}
        {invoice.noteToClient && (
          <p className="text-muted-foreground border-t pt-5 text-xs whitespace-pre-line">{invoice.noteToClient}</p>
        )}
        <div className="text-muted-foreground flex justify-between gap-5 border-t pt-5 text-[11px] leading-relaxed">
          <span>
            Payment accepted by [ACH / card / check].
            <br />
            Questions? [Email] · [Phone]
          </span>
          <span className="text-right">
            Thank you for trusting
            <br />
            {firmName} with your case.
          </span>
        </div>
      </div>

      <span className="text-muted-foreground px-1 text-xs">
        Draft watermark is removed when the invoice is confirmed.
      </span>
    </aside>
  );
}
